import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { companyRepository } from '../../infrastructure/authApiRepository';
import { useAuthContext } from '../../application/AuthContext';
import '../styles/auth.css';
import '../styles/company-profile.css';

/* ── 커스텀 드롭다운 컴포넌트 ── */
function Dropdown({ value, onChange, options, placeholder }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    const close = useCallback((e) => {
        if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }, []);

    useEffect(() => {
        if (open) document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open, close]);

    const selected = options.find(o => o === value);

    return (
        <div className="cp-dropdown" ref={ref}>
            <button
                type="button"
                className={`cp-dropdown__trigger${open ? ' cp-dropdown__trigger--open' : ''}${!selected ? ' cp-dropdown__trigger--empty' : ''}`}
                onClick={() => setOpen(v => !v)}
            >
                <span>{selected || placeholder}</span>
                <svg className={`cp-dropdown__arrow${open ? ' cp-dropdown__arrow--up' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>
            {open && (
                <ul className="cp-dropdown__list">
                    {options.map(o => (
                        <li
                            key={o}
                            className={`cp-dropdown__item${o === value ? ' cp-dropdown__item--active' : ''}`}
                            onMouseDown={() => { onChange(o); setOpen(false); }}
                        >
                            {o}
                            {o === value && (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function loadDaumPostcode(callback) {
    if (window.daum?.Postcode) { callback(); return; }
    const script = document.createElement('script');
    script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
    script.onload = callback;
    document.head.appendChild(script);
}

const INDUSTRY_OPTIONS = [
    'IT, 컨텐츠', '판매, 유통', '제조', '전문, 과학기술', '기타 서비스업', '금융',
    '교육', '부동산', '예술, 스포츠, 여가', '물류, 운송', '건설', '보건, 사회복지',
    '게임', '숙박, 음식점', '사업지원', '농림어업', '상수도, 환경', '공공행정, 국방',
    '국제, 외국기관', '전기, 가스', '광업', '가사, 가정',
];

const EMPLOYEE_COUNT_OPTIONS = [
    '1~9명', '10~49명', '50~99명', '100~299명', '300명 이상',
];

export function CompanyProfilePage() {
    const { member, accessToken, updateCompanyInfo } = useAuthContext();
    const navigate = useNavigate();

    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [fieldError, setFieldError] = useState({});
    const [form, setForm] = useState({
        companyAddress: '',
        companyAddressDetail: '',
        industry: '',
        employeeCount: '',
    });

    const addressRef = useRef(null);

    useEffect(() => {
        if (!member || member.role !== 'COMPANY') {
            navigate('/', { replace: true });
            return;
        }
        fetchProfile();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    async function fetchProfile() {
        try {
            const data = await companyRepository.getProfile(accessToken);
            setProfile(data);
            setForm({
                companyAddress: data.companyAddress || '',
                companyAddressDetail: data.companyAddressDetail || '',
                industry: data.industry || '',
                employeeCount: data.employeeCount || '',
            });
        } catch {
            setError('프로필 정보를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    }

    const set = (field) => (e) => {
        setForm((f) => ({ ...f, [field]: e.target.value }));
        if (fieldError[field]) setFieldError((fe) => ({ ...fe, [field]: '' }));
    };

    function openPostcode() {
        loadDaumPostcode(() => {
            new window.daum.Postcode({
                oncomplete(result) {
                    const addr = result.roadAddress || result.jibunAddress;
                    setForm((f) => ({ ...f, companyAddress: addr, companyAddressDetail: '' }));
                    setFieldError((fe) => ({ ...fe, companyAddress: '' }));
                    setTimeout(() => addressRef.current?.focus(), 100);
                },
            }).open();
        });
    }

    function validate() {
        const errs = {};
        if (!form.companyAddress?.trim()) errs.companyAddress = '회사 주소를 입력해주세요.';
        return errs;
    }

    async function handleSubmit(e) {
        e.preventDefault();
        const errs = validate();
        setFieldError(errs);
        if (Object.keys(errs).length > 0) return;
        setError('');
        setSubmitting(true);
        try {
            const data = await companyRepository.updateProfile(accessToken, form);
            setProfile(data);
            updateCompanyInfo({ companyStatus: data.companyStatus });
        } catch (err) {
            setError(err.message || '저장에 실패했습니다.');
        } finally {
            setSubmitting(false);
        }
    }

    if (loading) {
        return (
            <div className="cp-page">
                <div className="cp-card">
                    <div className="cp-loading">프로필 정보를 불러오는 중...</div>
                </div>
            </div>
        );
    }

    // 기업 인증 심사 중
    if (profile?.companyStatus === 'PENDING') {
        return (
            <div className="cp-page">
                <div className="cp-card">
                    <Link to="/" className="auth-card__logo">
                        <img src="/images/logo/main_logo.png" alt="RISING BROKERS" />
                    </Link>
                    <div className="auth-success">

                        <div className="auth-success__title">기업 인증 심사 중입니다</div>
                        <div className="auth-success__desc">
                            제출하신 기업 정보를 검토 중입니다.<br />
                            승인 완료 후 서비스 이용이 가능합니다.<br />
                            보통 1~2 영업일 내 처리됩니다.
                        </div>
                        <Link to="/" className="auth-submit" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
                            홈으로
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const isRejected = profile?.companyStatus === 'REJECTED';

    return (
        <div className="cp-page">
            <div className="cp-card">
                <Link to="/" className="auth-card__logo">
                    <img src="/images/logo/main_logo.png" alt="RISING BROKERS" />
                </Link>
                <div className="auth-card__title">기업 정보</div>
                <div className="auth-card__sub">기업 정보를 입력해주세요</div>

                {isRejected && profile.rejectedReason && (
                    <div className="auth-alert auth-alert--error" style={{ marginBottom: 20 }}>
                        <strong>반려 사유:</strong> {profile.rejectedReason}
                    </div>
                )}

                {error && <div className="auth-alert auth-alert--error">{error}</div>}

                {/* 기업 기본 정보 (read-only) */}
                <div className="cp-info-box" style={{ marginBottom: 20 }}>
                    <div className="cp-info-row">
                        <span className="cp-info-label">기업명</span>
                        <span className="cp-info-value">{profile?.companyName}</span>
                    </div>
                    {profile?.industry && (
                        <div className="cp-info-row">
                            <span className="cp-info-label">산업</span>
                            <span className="cp-info-value">{profile.industry}</span>
                        </div>
                    )}
                </div>

                <form className="cp-form" onSubmit={handleSubmit} noValidate>
                        <div className="cp-section-title">추가 정보</div>

                        <div className="auth-field">
                            <label>산업</label>
                            <Dropdown
                                value={form.industry}
                                onChange={(v) => { setForm(f => ({ ...f, industry: v })); }}
                                options={INDUSTRY_OPTIONS}
                                placeholder="산업을 선택해주세요"
                            />
                        </div>

                        <div className="auth-field">
                            <label>직원 수</label>
                            <Dropdown
                                value={form.employeeCount}
                                onChange={(v) => { setForm(f => ({ ...f, employeeCount: v })); }}
                                options={EMPLOYEE_COUNT_OPTIONS}
                                placeholder="직원 수를 선택해주세요"
                            />
                        </div>

                        <div className="auth-field">
                            <label>회사 주소 <span className="cp-required">*</span></label>
                            <div className="cp-address-row">
                                <input
                                    className={`auth-input${fieldError.companyAddress ? ' auth-input--error' : ''}`}
                                    type="text"
                                    placeholder="주소 검색을 눌러 주소를 입력해주세요"
                                    value={form.companyAddress}
                                    readOnly
                                    onClick={openPostcode}
                                    style={{ cursor: 'pointer' }}
                                />
                                <button type="button" className="cp-address-btn" onClick={openPostcode}>
                                    주소 검색
                                </button>
                            </div>
                            {fieldError.companyAddress && <span className="auth-field__error">{fieldError.companyAddress}</span>}
                            <input
                                className="auth-input cp-address-detail"
                                type="text"
                                placeholder="상세 주소 (동, 호수 등)"
                                value={form.companyAddressDetail}
                                onChange={set('companyAddressDetail')}
                                ref={addressRef}
                            />
                        </div>

                        <button className="auth-submit" type="submit" disabled={submitting}>
                            {submitting ? '제출 중...' : isRejected ? '재제출하기' : '기업 인증 신청'}
                        </button>
                </form>
            </div>
        </div>
    );
}
